/** @jsxImportSource @emotion/react */
import { useSession } from '@quickbit/backoffice-core-frontend';
import {
  createStyles,
  Table,
  Typography,
  UtilityButton,
  useTheme,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { Permissions } from '../../../constants';
import {
  ContactItem,
  FileItem,
  useApproveSelfHostedProofOfOwnership,
} from '../ManualReview/api';
import { TravelRuleDetailsModal } from './TravelRuleDetailsModal';

interface Props {
  contacts: ContactItem[];
  refetch: () => void;
}

const headers = [
  { label: 'Merchant Name', cellKey: 'merchantName', sort: false },
  { label: 'Contact Label', cellKey: 'label', sort: false },
  { label: 'Address', cellKey: 'address', sort: false },
  { label: 'Currency', cellKey: 'currency', sort: false },
  { label: 'Files', cellKey: 'files', sort: false },
  { label: 'Travel Rule Details', cellKey: 'travelRuleDetails', sort: false },
  { label: 'Action', cellKey: 'action', sort: false },
];

export const ContactsTable = ({ contacts, refetch }: Props) => {
  const styles = useStyles();
  const [selectedTravelRuleData, setSelectedTravelRuleData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [showModal, setShowModal] = useState(false);

  if (contacts.length === 0) {
    return null;
  }

  return (
    <div css={styles.container}>
      {showModal && (
        <TravelRuleDetailsModal
          travelRuleData={selectedTravelRuleData}
          onClose={() => setShowModal(false)}
        />
      )}
      <Typography variant="subtitle">Contacts</Typography>
      <Table
        headers={headers}
        rows={contacts.map((contact) => ({
          value: {
            merchantName: contact.merchantName,
            label: contact.label,
            address: (
              <div css={styles.addressCell}>
                <Typography variant="bodySmallest">
                  {contact.address}
                </Typography>
              </div>
            ),
            currency: contact.currencyCode,
            files: <FileList files={contact.files} />,
            travelRuleDetails: (
              <UtilityButton
                text="Show details"
                variant="secondary"
                onClick={() => {
                  setSelectedTravelRuleData(contact.travelRuleData);
                  setShowModal(true);
                }}
              />
            ),
            action: (
              <ApproveButton
                merchantId={contact.merchantId}
                contactId={contact.contactId}
                refetch={refetch}
              />
            ),
          },
        }))}
      />
    </div>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  addressCell: {
    wordBreak: 'break-all',
  },
  fileList: {
    maxWidth: '200px',
  },
}));

const ApproveButton = ({
  merchantId,
  contactId,
  refetch,
}: {
  merchantId: string;
  contactId: string;
  refetch: () => void;
}) => {
  const { palette } = useTheme();
  const session = useSession();
  const {
    mutate: approveProofOfOwnership,
    isPending,
    error,
  } = useApproveSelfHostedProofOfOwnership({
    merchantId,
  });

  const canApprove =
    session.authenticated &&
    session.permissions.includes(Permissions.PROCESS_FCU_REFUNDS);

  const handleApprove = () => {
    approveProofOfOwnership(
      { recipientId: contactId },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };

  if (error) {
    return (
      <Typography color={palette.danger.default}>
        {error.errorMessage}
      </Typography>
    );
  }

  if (!canApprove) {
    return (
      <Typography variant="bodySmallest">Pending approval from FCU</Typography>
    );
  }

  return (
    <UtilityButton
      text="Approve"
      variant="primary"
      onClick={handleApprove}
      disabled={isPending}
    />
  );
};

const FileList = ({ files }: { files: FileItem[] }) => {
  const styles = useStyles();
  if (files.length === 0) {
    return <Typography variant="bodySmallest">No files</Typography>;
  }
  return (
    <>
      {files.map((file) => (
        <div key={file.name} css={styles.fileList}>
          <a href={file.url} target="_blank" rel="noreferrer">
            {file.name.split('_').slice(1).join('_')}
          </a>
        </div>
      ))}
    </>
  );
};
